import { lazy, Suspense } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { PrismLogo } from '../ui/PrismLogo'
import { DEV_AUTH_BYPASS } from '../../lib/devAuth'

const LoginPage = lazy(() => import('../../pages/LoginPage'))

interface AuthGuardProps {
  children: React.ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useIsAuthenticated()
  const { inProgress } = useMsal()

  // Local development without Entra (ADR-017); compiled out of production builds.
  if (DEV_AUTH_BYPASS) return <>{children}</>

  // MSAL is processing a redirect return — show minimal loader
  if (inProgress === InteractionStatus.HandleRedirect) {
    return <AuthLoader message="Completing sign in..." />
  }

  // Not authenticated — show branded login page
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<AuthLoader message="" />}>
        <LoginPage />
      </Suspense>
    )
  }

  return <>{children}</>
}

function AuthLoader({ message }: { message: string }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '20px',
      backgroundColor: 'var(--bg-base)',
    }}>
      <PrismLogo size="lg" variant="full" />
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: 'var(--text-secondary)',
        fontSize: '13px',
      }}>
        <div style={{
          width: '16px',
          height: '16px',
          border: '2px solid var(--border-default)',
          borderTopColor: 'var(--brand-coral)',
          borderRadius: '50%',
          animation: 'spin 0.7s linear infinite',
          flexShrink: 0,
        }} />
        {message}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
